import {
  DEFAULT_GIFTCARD_URL,
  DEFAULT_HOME_URL,
  DEFAULT_LOGO_SRC,
  FOOTER_ICON_MAP,
  type FooterContactGroup,
  type FooterContent,
  type FooterCountryLink,
  type FooterLink,
  type FooterSection,
  type FooterSocialLink,
} from '../lib/footer.config';

import type { FooterWidgetAttribute, FooterWidgetProps } from './types';

export const OBSERVED_FOOTER_WIDGET_ATTRIBUTES: FooterWidgetAttribute[] = [
  'logo-src',
  'home-url',
  'giftcard-url',
  'newsletter-submit-url',
  'newsletter-recaptcha-site-key',
  'newsletter-recaptcha-action',
  'newsletter-source',
  'page-url',
  'hide-newsletter',
  'hide-giftcard',
  'footer-content',
];

const DEFAULT_RECAPTCHA_ACTION = 'newsletter';

function readBooleanAttribute(element: HTMLElement, name: string): boolean {
  const value = element.getAttribute(name);
  return value !== null && value !== 'false';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function readTarget(value: unknown): FooterLink['target'] | undefined {
  return value === '_blank' || value === '_self' ? value : undefined;
}

function readLink(value: unknown): FooterLink | undefined {
  if (!isRecord(value)) return undefined;

  const href = readString(value.href);
  const label = readString(value.label);
  if (!href || !label) return undefined;

  return {
    href,
    label,
    badge: readString(value.badge),
    target: readTarget(value.target),
  };
}

function readLinks(value: unknown): FooterLink[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const links = value.map(readLink).filter((link): link is FooterLink => Boolean(link));
  return links.length > 0 ? links : undefined;
}

function readSections(value: unknown): FooterSection[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const sections = value
    .map((section): FooterSection | undefined => {
      if (!isRecord(section)) return undefined;

      const title = readString(section.title);
      const links = readLinks(section.links);
      if (!title || !links) return undefined;

      return { title, links };
    })
    .filter((section): section is FooterSection => Boolean(section));

  return sections.length > 0 ? sections : undefined;
}

function readCountryLinks(value: unknown): FooterCountryLink[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const countries = value
    .map((country): FooterCountryLink | undefined => {
      if (!isRecord(country)) return undefined;

      const flagSrc = readString(country.flagSrc);
      const href = readString(country.href);
      const label = readString(country.label);
      if (!flagSrc || !href || !label) return undefined;

      return { flagSrc, href, label };
    })
    .filter((country): country is FooterCountryLink => Boolean(country));

  return countries.length > 0 ? countries : undefined;
}

function readContactGroups(value: unknown): FooterContactGroup[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const groups = value
    .map((group): FooterContactGroup | undefined => {
      if (!isRecord(group)) return undefined;

      const title = readString(group.title);
      const rawIcon = readString(group.icon);
      const icon =
        rawIcon && rawIcon in FOOTER_ICON_MAP ? (rawIcon as FooterContactGroup['icon']) : undefined;
      const items = readLinks(group.items);
      if (!title || !items) return undefined;

      return { title, icon, items };
    })
    .filter((group): group is FooterContactGroup => Boolean(group));

  return groups.length > 0 ? groups : undefined;
}

function readSocialLinks(value: unknown): FooterSocialLink[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const socialLinks = value
    .map((social): FooterSocialLink | undefined => {
      const link = readLink(social);
      if (!link || !isRecord(social)) return undefined;

      const rawIcon = readString(social.icon);
      const icon =
        rawIcon && rawIcon in FOOTER_ICON_MAP ? (rawIcon as FooterSocialLink['icon']) : undefined;

      return {
        href: link.href,
        icon,
        label: link.label,
      };
    })
    .filter((social): social is FooterSocialLink => Boolean(social));

  return socialLinks.length > 0 ? socialLinks : undefined;
}

export function parseFooterContent(raw: string | null): FooterContent | undefined {
  if (!raw) return undefined;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return undefined;

    const content: FooterContent = {
      contactGroups: readContactGroups(parsed.contactGroups),
      copyrightText: readString(parsed.copyrightText),
      countryLinks: readCountryLinks(parsed.countryLinks),
      description: readString(parsed.description),
      extraLinks: readLinks(parsed.extraLinks),
      legalText: readString(parsed.legalText),
      programSections: readSections(parsed.programSections),
      resourceSections: readSections(parsed.resourceSections),
      socialLinks: readSocialLinks(parsed.socialLinks),
    };

    return Object.values(content).some(Boolean) ? content : undefined;
  } catch {
    return undefined;
  }
}

export function readFooterWidgetProps(element: HTMLElement): FooterWidgetProps {
  return {
    content: parseFooterContent(element.getAttribute('footer-content')),
    logoSrc: element.getAttribute('logo-src') ?? DEFAULT_LOGO_SRC,
    homeUrl: element.getAttribute('home-url') ?? DEFAULT_HOME_URL,
    giftcardUrl: element.getAttribute('giftcard-url') ?? DEFAULT_GIFTCARD_URL,
    newsletterSubmitUrl: element.getAttribute('newsletter-submit-url') ?? undefined,
    newsletterRecaptchaSiteKey: element.getAttribute('newsletter-recaptcha-site-key') ?? undefined,
    newsletterRecaptchaAction:
      element.getAttribute('newsletter-recaptcha-action') ?? DEFAULT_RECAPTCHA_ACTION,
    newsletterSource: element.getAttribute('newsletter-source') ?? undefined,
    pageUrl: element.getAttribute('page-url') ?? window.location.href,
    hideNewsletter: readBooleanAttribute(element, 'hide-newsletter'),
    hideGiftcard: readBooleanAttribute(element, 'hide-giftcard'),
  };
}
